/**
 * Emperor Marble AI Chatbot
 *
 * Persistent bottom-bar chatbot for product and collection pages.
 * Connects to FMM-AOD /api/chat endpoint via SSE streaming.
 * Persists conversation history in localStorage.
 * On collection pages, filters the product grid when AI returns search results.
 *
 * Config injected via window.__CHATBOT_CONFIG:
 *   { apiUrl: string, placeholder: string, pageType: string }
 *
 * Dependencies: None (standalone vanilla JS).
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
   * Constants
   * ----------------------------------------------------------------------- */

  var SELECTORS = {
    BAR: '[data-chatbot-bar]',
    EXPAND: '[data-chatbot-expand]',
    PANEL: '[data-chatbot-panel]',
    BACKDROP: '[data-chatbot-backdrop]',
    MINIMIZE: '[data-chatbot-minimize]',
    MAXIMIZE: '[data-chatbot-maximize]',
    CLEAR: '[data-chatbot-clear]',
    MESSAGES: '[data-chatbot-messages]',
    INPUT: '[data-chatbot-input]',
    SEND: '[data-chatbot-send]',
    LOADING: '[data-chatbot-loading]',
    FILTER_TEMPLATE: '[data-chatbot-filter-template]',
    FILTER_BANNER: '[data-chatbot-filter-banner]',
    FILTER_COUNT: '[data-chatbot-filter-count]',
    FILTER_TOTAL: '[data-chatbot-filter-total]',
    FILTER_CLEAR: '[data-chatbot-filter-clear]',
    EMPTY_TEMPLATE: '[data-chatbot-empty-template]',
    PRODUCT_GRID: '#product-grid',
    PRODUCT_CARD: '.collection-product-card'
  };

  var CLASSES = {
    PANEL_OPEN: 'chatbot-panel--open',
    PANEL_FULLSCREEN: 'chatbot-panel--fullscreen',
    BACKDROP_VISIBLE: 'chatbot-backdrop--visible',
    MSG_BASE: 'chatbot-message',
    MSG_USER: 'chatbot-message--user',
    MSG_ASSISTANT: 'chatbot-message--assistant',
    MSG_TOOL: 'chatbot-message--tool',
    MSG_ERROR: 'chatbot-message--error',
    AI_HIDDEN: 'chatbot-ai-hidden',
    BODY_PAD: 'chatbot-body-pad',
    EMPTY: 'chatbot-messages__empty'
  };

  var STORAGE_KEYS = {
    HISTORY: 'emperor_chat_history',
    MINIMIZED: 'emperor_chat_minimized',
    FULLSCREEN: 'emperor_chat_fullscreen'
  };

  var MAX_HISTORY = 50;
  var HISTORY_CONTEXT_SIZE = 20;
  var SSE_DATA_PREFIX = 'data: ';
  var SSE_DATA_PREFIX_LEN = 6;
  var SSE_DONE_SIGNAL = '[DONE]';

  /* -----------------------------------------------------------------------
   * Minimal Markdown → HTML converter (safe subset)
   * ----------------------------------------------------------------------- */

  function renderMarkdown(text) {
    if (!text) return '';

    // Escape HTML entities first
    var html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Remove markdown table blocks (already shown as cards)
    html = html.replace(/\|[^\n]*\|[\s]*\n\|[-\s|:]*\|[\s]*\n(\|[^\n]*\|[\s]*\n?)*/g, '');

    // Bold: **text**
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Italic: *text* (skip already-converted <strong> tags)
    html = html.replace(/(?:^|[^*])\*([^*]+)\*(?:[^*]|$)/g, function (m, inner) {
      return m.replace('*' + inner + '*', '<em>' + inner + '</em>');
    });

    // Numbered lists: lines starting with "1. ", "2. ", etc.
    html = html.replace(/(^|\n)(\d+\.\s+.+(?:\n\d+\.\s+.+)*)/g, function (match, prefix) {
      var items = match.trim().split('\n').map(function (line) {
        return '<li>' + line.replace(/^\d+\.\s+/, '') + '</li>';
      }).join('');
      return prefix + '<ol>' + items + '</ol>';
    });

    // Paragraphs: double line breaks
    html = html.replace(/\n{2,}/g, '</p><p>');

    // Single line breaks → <br>
    html = html.replace(/\n/g, '<br>');

    // Wrap in <p> and clean up empty paragraphs
    html = '<p>' + html + '</p>';
    html = html.replace(/<p>\s*<\/p>/g, '');
    html = html.replace(/<p>\s*<br>/g, '<p>');
    html = html.replace(/<br>\s*<\/p>/g, '</p>');

    return html.trim();
  }

  /* -----------------------------------------------------------------------
   * Constructor
   * ----------------------------------------------------------------------- */

  /**
   * @constructor
   * @param {Object} config - Chatbot configuration from window.__CHATBOT_CONFIG
   */
  function EmperorChatbot(config) {
    this.apiUrl = config.apiUrl;
    this.apiKey = config.apiKey || '';
    this.placeholder = config.placeholder || '';
    this.pageType = config.pageType || '';
    this.history = [];
    this.isExpanded = false;
    this.isFullscreen = false;
    this.isSending = false;
    this.currentAssistantEl = null;
    this.currentAssistantText = '';
    this.toolStatusEl = null;
    this.filterBannerEl = null;
    this.filterClearBound = false;
    this.productCache = {};
    this.els = {};
  }

  /* -----------------------------------------------------------------------
   * Initialization
   * ----------------------------------------------------------------------- */

  /** Bootstraps the chatbot: queries DOM, loads state, binds events. */
  EmperorChatbot.prototype.init = function () {
    if (!this._queryElements()) return;
    this.loadHistory();
    this.renderHistory();
    this._applyInitialState();
    this._showBar();
    this._bindEvents();
  };

  /**
   * Queries all required DOM elements and stores references.
   * @returns {boolean} true if all required elements found
   * @private
   */
  EmperorChatbot.prototype._queryElements = function () {
    var keys = ['BAR', 'EXPAND', 'PANEL', 'BACKDROP', 'MINIMIZE', 'MAXIMIZE', 'CLEAR', 'MESSAGES', 'INPUT', 'SEND', 'LOADING'];

    for (var i = 0; i < keys.length; i++) {
      var el = document.querySelector(SELECTORS[keys[i]]);
      if (!el) {
        console.warn('EmperorChatbot: missing element ' + SELECTORS[keys[i]]);
        return false;
      }
      this.els[keys[i]] = el;
    }

    return true;
  };

  /**
   * Applies initial expanded/minimized state from localStorage.
   * @private
   */
  EmperorChatbot.prototype._applyInitialState = function () {
    var wasMinimized = this._getMinimizedState();

    if (!wasMinimized && this.history.length > 0) {
      this.expand();
    }
  };

  /**
   * Shows the bottom bar and adds body padding.
   * @private
   */
  EmperorChatbot.prototype._showBar = function () {
    this.els.BAR.removeAttribute('hidden');
    document.body.classList.add(CLASSES.BODY_PAD);

    if (this.placeholder) {
      this.els.INPUT.setAttribute('placeholder', this.placeholder);
    }
  };

  /* -----------------------------------------------------------------------
   * Event Binding
   * ----------------------------------------------------------------------- */

  /**
   * Binds all UI event listeners.
   * @private
   */
  EmperorChatbot.prototype._bindEvents = function () {
    this.els.EXPAND.addEventListener('click', this.expand.bind(this));
    this.els.MINIMIZE.addEventListener('click', this.minimize.bind(this));
    this.els.MAXIMIZE.addEventListener('click', this._onMaximizeClick.bind(this));
    this.els.BACKDROP.addEventListener('click', this.minimize.bind(this));
    this.els.CLEAR.addEventListener('click', this.clearConversation.bind(this));
    this.els.SEND.addEventListener('click', this.handleSend.bind(this));
    this.els.INPUT.addEventListener('keydown', this._onInputKeydown.bind(this));
    document.addEventListener('keydown', this._onDocumentKeydown.bind(this));
  };

  /**
   * Handles keydown on the chat input field.
   * @param {KeyboardEvent} e
   * @private
   */
  EmperorChatbot.prototype._onInputKeydown = function (e) {
    if (e.key === 'Enter' && !this.isSending) {
      e.preventDefault();
      this.handleSend();
    }
  };

  /**
   * Handles Escape key to close the chat panel.
   * @param {KeyboardEvent} e
   * @private
   */
  EmperorChatbot.prototype._onDocumentKeydown = function (e) {
    if (e.key === 'Escape' && this.isExpanded) {
      this.minimize();
    }
  };

  /* -----------------------------------------------------------------------
   * UI: Expand / Minimize / Fullscreen
   * ----------------------------------------------------------------------- */

  /** Expands the chat panel. */
  EmperorChatbot.prototype.expand = function () {
    this.isExpanded = true;
    this.els.BAR.setAttribute('hidden', '');
    this.els.PANEL.removeAttribute('hidden');
    this._showBackdrop();

    // Restore fullscreen preference
    if (this._getFullscreenState()) {
      this.els.PANEL.classList.add(CLASSES.PANEL_FULLSCREEN);
      this.isFullscreen = true;
      this._syncMaximizeButtons();
    }

    // Force reflow before adding the open class so the CSS transition fires
    void this.els.PANEL.offsetHeight;
    this.els.PANEL.classList.add(CLASSES.PANEL_OPEN);
    this._saveMinimizedState(false);
    this.scrollToBottom();
    this.els.INPUT.focus();
  };

  /** Minimizes the chat panel back to the bar. */
  EmperorChatbot.prototype.minimize = function () {
    this.isExpanded = false;
    this.els.PANEL.classList.remove(CLASSES.PANEL_OPEN);
    this._hideBackdrop();
    this._saveMinimizedState(true);
    this.els.BAR.removeAttribute('hidden');

    // Wait for transition to complete before hiding
    var panel = this.els.PANEL;
    var fullscreenClass = CLASSES.PANEL_FULLSCREEN;
    setTimeout(function () {
      if (!panel.classList.contains(CLASSES.PANEL_OPEN)) {
        panel.setAttribute('hidden', '');
        panel.classList.remove(fullscreenClass);
      }
    }, 400);
  };

  /**
   * Handles maximize/restore button click — toggles based on current state.
   * @private
   */
  EmperorChatbot.prototype._onMaximizeClick = function () {
    this.toggleFullscreen(!this.isFullscreen);
  };

  /**
   * Toggles fullscreen mode on or off.
   * @param {boolean} enable - true to enter fullscreen, false to restore
   */
  EmperorChatbot.prototype.toggleFullscreen = function (enable) {
    this.isFullscreen = enable;
    this.els.PANEL.classList.toggle(CLASSES.PANEL_FULLSCREEN, enable);
    this._saveFullscreenState(enable);
    this._syncMaximizeButtons();
    this.scrollToBottom();
  };

  /**
   * Swaps the maximize/restore icon and label within the single toggle button.
   * @private
   */
  EmperorChatbot.prototype._syncMaximizeButtons = function () {
    var btn = this.els.MAXIMIZE;
    var maximizeIcon = btn.querySelector('.chatbot-icon-maximize');
    var restoreIcon = btn.querySelector('.chatbot-icon-restore');

    if (this.isFullscreen) {
      if (maximizeIcon) maximizeIcon.setAttribute('hidden', '');
      if (restoreIcon) restoreIcon.removeAttribute('hidden');
      btn.setAttribute('aria-label', 'Restore chat size');
      btn.setAttribute('title', 'Restore');
    } else {
      if (maximizeIcon) maximizeIcon.removeAttribute('hidden');
      if (restoreIcon) restoreIcon.setAttribute('hidden', '');
      btn.setAttribute('aria-label', 'Maximize chat');
      btn.setAttribute('title', 'Maximize');
    }
  };

  /* -----------------------------------------------------------------------
   * UI: Backdrop
   * ----------------------------------------------------------------------- */

  /** Shows the backdrop overlay. @private */
  EmperorChatbot.prototype._showBackdrop = function () {
    this.els.BACKDROP.removeAttribute('hidden');
    void this.els.BACKDROP.offsetHeight;
    this.els.BACKDROP.classList.add(CLASSES.BACKDROP_VISIBLE);
  };

  /** Hides the backdrop overlay. @private */
  EmperorChatbot.prototype._hideBackdrop = function () {
    this.els.BACKDROP.classList.remove(CLASSES.BACKDROP_VISIBLE);
    var backdrop = this.els.BACKDROP;
    setTimeout(function () {
      if (!backdrop.classList.contains(CLASSES.BACKDROP_VISIBLE)) {
        backdrop.setAttribute('hidden', '');
      }
    }, 350);
  };

  /* -----------------------------------------------------------------------
   * UI: Send Message
   * ----------------------------------------------------------------------- */

  /** Handles the send action (button click or Enter key). */
  EmperorChatbot.prototype.handleSend = function () {
    var text = this.els.INPUT.value.trim();
    if (!text || this.isSending) return;

    this.els.INPUT.value = '';
    this.addMessage('user', text);
    this.sendMessage(text);
  };

  /* -----------------------------------------------------------------------
   * Message Rendering
   * ----------------------------------------------------------------------- */

  /**
   * Adds a message to the UI and (optionally) to history.
   * @param {string} role - 'user', 'assistant', 'error', or 'tool'
   * @param {string} content - Message text
   */
  EmperorChatbot.prototype.addMessage = function (role, content) {
    this._removeEmptyState();
    var el = this.createMessageElement(role);
    if (role === 'assistant') {
      el.innerHTML = renderMarkdown(content);
    } else {
      el.textContent = content;
    }
    this.els.MESSAGES.appendChild(el);

    if (role !== 'error' && role !== 'tool') {
      this._pushHistory(role, content);
    }

    this.scrollToBottom();
  };

  /**
   * Creates a message DOM element with appropriate classes.
   * @param {string} role - Message role
   * @returns {HTMLDivElement}
   */
  EmperorChatbot.prototype.createMessageElement = function (role) {
    var el = document.createElement('div');
    el.className = CLASSES.MSG_BASE + ' ' + this._roleClass(role);
    return el;
  };

  /**
   * Maps a role string to the corresponding CSS class.
   * @param {string} role
   * @returns {string}
   * @private
   */
  EmperorChatbot.prototype._roleClass = function (role) {
    var map = {
      user: CLASSES.MSG_USER,
      assistant: CLASSES.MSG_ASSISTANT,
      tool: CLASSES.MSG_TOOL,
      error: CLASSES.MSG_ERROR
    };
    return map[role] || CLASSES.MSG_ASSISTANT;
  };

  /**
   * Removes the empty state placeholder if present.
   * @private
   */
  EmperorChatbot.prototype._removeEmptyState = function () {
    var empty = this.els.MESSAGES.querySelector('.' + CLASSES.EMPTY);
    if (empty) empty.remove();
  };

  /** Shows the empty state placeholder from the Liquid template. */
  EmperorChatbot.prototype.showEmptyState = function () {
    var template = document.querySelector(SELECTORS.EMPTY_TEMPLATE);
    if (template) {
      this.els.MESSAGES.appendChild(template.content.cloneNode(true));
    } else {
      var el = document.createElement('div');
      el.className = CLASSES.EMPTY;
      el.textContent = 'Ask about our marble collection';
      this.els.MESSAGES.appendChild(el);
    }
  };

  /* -----------------------------------------------------------------------
   * SSE Streaming: sendMessage
   * ----------------------------------------------------------------------- */

  /**
   * Sends a message to the FMM-AOD chat endpoint and processes the SSE stream.
   * @param {string} text - User message text
   */
  EmperorChatbot.prototype.sendMessage = async function (text) {
    this.isSending = true;
    this.els.SEND.disabled = true;
    this.showLoading();
    this.currentAssistantText = '';
    this.currentAssistantEl = null;

    var payload = this._buildPayload(text);

    try {
      var headers = { 'Content-Type': 'application/json' };
      if (this.apiKey) headers['X-API-Key'] = this.apiKey;

      var response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        this._handleHttpError(response.status);
        return;
      }

      await this.processSSEStream(response);
    } catch (err) {
      this.addMessage('error', 'Unable to connect. Please check your internet connection and try again.');
      this._finishSending();
    }
  };

  /**
   * Builds the API request payload with conversation history.
   * @param {string} text - Current user message
   * @returns {Object}
   * @private
   */
  EmperorChatbot.prototype._buildPayload = function (text) {
    // Build AI SDK UIMessage format: { messages: [{role, content}] }
    var messages = this.history
      .filter(function (m) { return m.role === 'user' || m.role === 'assistant'; })
      .slice(-(HISTORY_CONTEXT_SIZE + 1))
      .map(function (m) { return { role: m.role, content: m.content }; });

    // Remove the last entry (the current user message we just added to history)
    if (messages.length > 0) {
      messages.pop();
    }

    // Add the current message
    messages.push({ role: 'user', content: text });

    return { messages: messages, mode: 'catalog' };
  };

  /**
   * Handles non-OK HTTP responses with user-friendly messages.
   * @param {number} status - HTTP status code
   * @private
   */
  EmperorChatbot.prototype._handleHttpError = function (status) {
    var errorText = status === 429
      ? 'Too many requests. Please wait a moment and try again.'
      : 'Connection error (HTTP ' + status + '). Please try again.';
    this.addMessage('error', errorText);
    this._finishSending();
  };

  /**
   * Resets sending state and UI after a message exchange completes.
   * @private
   */
  EmperorChatbot.prototype._finishSending = function () {
    this.hideLoading();
    this.isSending = false;
    this.els.SEND.disabled = false;
  };

  /* -----------------------------------------------------------------------
   * SSE Streaming: processSSEStream
   * ----------------------------------------------------------------------- */

  /**
   * Reads the SSE response body as a stream and processes events.
   * @param {Response} response - Fetch response with readable body
   */
  EmperorChatbot.prototype.processSSEStream = async function (response) {
    var reader = response.body.getReader();
    var decoder = new TextDecoder();
    var buffer = '';
    var self = this;

    try {
      while (true) {
        var result = await reader.read();
        if (result.done) break;

        buffer += decoder.decode(result.value, { stream: true });
        var lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (var i = 0; i < lines.length; i++) {
          self._processSSELine(lines[i]);
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        self._processSSELine(buffer);
      }
    } catch (err) {
      self._handleStreamError();
    }

    self._finishSending();
  };

  /**
   * Processes a single SSE line.
   * @param {string} rawLine - Raw line from the SSE stream
   * @private
   */
  EmperorChatbot.prototype._processSSELine = function (rawLine) {
    var line = rawLine.trim();
    if (!line || !line.startsWith(SSE_DATA_PREFIX)) return;

    var data = line.substring(SSE_DATA_PREFIX_LEN);

    if (data === SSE_DONE_SIGNAL) {
      this.finalizeAssistantMessage();
      return;
    }

    try {
      var event = JSON.parse(data);
      this.handleSSEEvent(event);
    } catch (e) {
      // Skip malformed JSON lines
    }
  };

  /**
   * Handles a stream interruption error.
   * @private
   */
  EmperorChatbot.prototype._handleStreamError = function () {
    if (this.currentAssistantText) {
      this.finalizeAssistantMessage();
    } else {
      this.addMessage('error', 'Stream interrupted. Please try again.');
    }
  };

  /* -----------------------------------------------------------------------
   * SSE Streaming: handleSSEEvent
   * ----------------------------------------------------------------------- */

  /**
   * Routes a parsed SSE event to the appropriate handler.
   * @param {Object} event - Parsed SSE event object
   */
  EmperorChatbot.prototype.handleSSEEvent = function (event) {
    switch (event.type) {
      // AI SDK UIMessage stream format
      case 'text-delta':
        this._handleTextDelta(event);
        break;
      case 'tool-input-start':
        this._handleToolCall(event);
        break;
      case 'tool-output-available':
        this._handleToolResult(event);
        break;
      case 'error':
        this.addMessage('error', event.errorText || 'Something went wrong. Please try again.');
        this._finishSending();
        break;
      case 'start':
      case 'text-start':
      case 'text-end':
      case 'tool-input-delta':
      case 'tool-input-available':
      case 'start-step':
      case 'finish-step':
      case 'finish':
        // Informational events — no action needed
        break;
    }
  };

  /**
   * Handles a text-delta SSE event (streaming token).
   * @param {Object} event - { type: 'text-delta', textDelta: string }
   * @private
   */
  EmperorChatbot.prototype._handleTextDelta = function (event) {
    this.hideLoading();

    if (!this.currentAssistantEl) {
      this.currentAssistantEl = this.createMessageElement('assistant');
      this.els.MESSAGES.appendChild(this.currentAssistantEl);
      this._removeEmptyState();
    }

    // AI SDK UIMessage stream uses 'delta', legacy used 'textDelta'
    this.currentAssistantText += (event.delta || event.textDelta || '');
    this.currentAssistantEl.innerHTML = renderMarkdown(this.currentAssistantText);
    this.scrollToBottom();
  };

  /**
   * Handles a tool-call SSE event (AI invoking a tool).
   * @param {Object} event - { type: 'tool-call', toolCall: { toolName: string } }
   * @private
   */
  EmperorChatbot.prototype._handleToolCall = function (event) {
    // AI SDK UIMessage stream: toolName is on event directly
    var toolName = event.toolName || (event.toolCall && event.toolCall.toolName);

    if (toolName === 'search_catalog' || toolName === 'filter_catalog') {
      this.showToolStatus('Searching catalog...');
    } else if (toolName === 'check_stock') {
      this.showToolStatus('Checking availability...');
    } else if (toolName === 'interpret_color') {
      this.showToolStatus('Interpreting color...');
    } else if (toolName) {
      this.showToolStatus('Processing...');
    }
  };

  /**
   * Handles a tool-result SSE event (tool returned data).
   * @param {Object} event - { type: 'tool-result', toolResult: Object }
   * @private
   */
  EmperorChatbot.prototype._handleToolResult = function (event) {
    this.removeToolStatus();

    // AI SDK UIMessage stream: output is on event directly
    var raw = event.output || (event.toolResult && event.toolResult.result);
    if (!raw) return;

    var parsed;
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      return;
    }

    var data = parsed.data || parsed;

    if (data.sampleBlocks && data.sampleBlocks.length > 0) {
      var stoneNames = this._extractUniqueStoneNames(data.sampleBlocks);
      this.handleSearchResults(stoneNames);
      this.fetchAndRenderBlockCards(data.sampleBlocks);
    } else if (data.stone_names && data.stone_names.length > 0) {
      this.handleSearchResults(data.stone_names);
    }
  };

  /* -----------------------------------------------------------------------
   * SSE Streaming: Finalize
   * ----------------------------------------------------------------------- */

  /** Saves the completed assistant message to history. */
  EmperorChatbot.prototype.finalizeAssistantMessage = function () {
    if (!this.currentAssistantText) return;

    this._pushHistory('assistant', this.currentAssistantText);
    this.currentAssistantEl = null;
    this.currentAssistantText = '';
  };

  /* -----------------------------------------------------------------------
   * Tool Status Indicator
   * ----------------------------------------------------------------------- */

  /**
   * Shows a transient tool status message (e.g., "Searching catalog...").
   * @param {string} text - Status text to display
   */
  EmperorChatbot.prototype.showToolStatus = function (text) {
    this.removeToolStatus();
    var el = this.createMessageElement('tool');
    el.textContent = text;
    this.els.MESSAGES.appendChild(el);
    this.toolStatusEl = el;
    this.scrollToBottom();
  };

  /** Removes the current tool status message from the DOM. */
  EmperorChatbot.prototype.removeToolStatus = function () {
    if (this.toolStatusEl) {
      this.toolStatusEl.remove();
      this.toolStatusEl = null;
    }
  };

  /* -----------------------------------------------------------------------
   * Loading Indicator
   * ----------------------------------------------------------------------- */

  /** Shows the loading dots indicator. */
  EmperorChatbot.prototype.showLoading = function () {
    this.els.LOADING.removeAttribute('hidden');
    this.scrollToBottom();
  };

  /** Hides the loading dots indicator. */
  EmperorChatbot.prototype.hideLoading = function () {
    this.els.LOADING.setAttribute('hidden', '');
  };

  /* -----------------------------------------------------------------------
   * Collection Page Filtering
   * ----------------------------------------------------------------------- */

  /**
   * Receives search results and applies filter on collection pages.
   * @param {string[]} stoneNames - Array of matching stone names from AI
   */
  EmperorChatbot.prototype.handleSearchResults = function (stoneNames) {
    if (this.pageType !== 'collection') return;
    this.applyFilter(stoneNames);
  };

  /**
   * Hides non-matching product cards and shows the filter banner.
   * @param {string[]} stoneNames - Matching stone names from AI search
   */
  EmperorChatbot.prototype.applyFilter = function (stoneNames) {
    var grid = document.querySelector(SELECTORS.PRODUCT_GRID);
    if (!grid) return;

    var cards = grid.querySelectorAll(SELECTORS.PRODUCT_CARD + '[data-stone-name]');
    var matchingStones = stoneNames.map(function (s) {
      return s.toLowerCase();
    });
    var visibleCount = 0;

    for (var i = 0; i < cards.length; i++) {
      var cardStoneName = (cards[i].getAttribute('data-stone-name') || '').toLowerCase();
      var isMatch = this._isStoneMatch(cardStoneName, matchingStones);
      cards[i].classList.toggle(CLASSES.AI_HIDDEN, !isMatch);
      if (isMatch) visibleCount++;
    }

    this.showFilterBanner(visibleCount, cards.length);
  };

  /**
   * Checks if a card's stone name matches any of the search results.
   * Uses bidirectional includes matching for flexibility.
   * @param {string} cardName - Lowercased stone name from the product card
   * @param {string[]} matchingStones - Lowercased stone names from AI
   * @returns {boolean}
   * @private
   */
  EmperorChatbot.prototype._isStoneMatch = function (cardName, matchingStones) {
    if (!cardName) return false;

    for (var i = 0; i < matchingStones.length; i++) {
      if (cardName.indexOf(matchingStones[i]) !== -1 || matchingStones[i].indexOf(cardName) !== -1) {
        return true;
      }
    }

    return false;
  };

  /**
   * Shows the filter banner above the product grid.
   * Clones from template on first use, then updates counts.
   * @param {number} visibleCount - Number of visible products
   * @param {number} totalCount - Total number of products with data-stone-name
   */
  EmperorChatbot.prototype.showFilterBanner = function (visibleCount, totalCount) {
    if (!this.filterBannerEl) {
      this._cloneFilterBanner();
    }

    if (!this.filterBannerEl) return;

    var countEl = this.filterBannerEl.querySelector(SELECTORS.FILTER_COUNT);
    var totalEl = this.filterBannerEl.querySelector(SELECTORS.FILTER_TOTAL);

    if (countEl) countEl.textContent = visibleCount;
    if (totalEl) totalEl.textContent = totalCount;

    this.filterBannerEl.removeAttribute('hidden');
  };

  /**
   * Clones the filter banner from the template and inserts it before the product grid.
   * @private
   */
  EmperorChatbot.prototype._cloneFilterBanner = function () {
    var template = document.querySelector(SELECTORS.FILTER_TEMPLATE);
    var grid = document.querySelector(SELECTORS.PRODUCT_GRID);
    if (!template || !grid) return;

    var clone = template.content.cloneNode(true);
    var banner = clone.querySelector(SELECTORS.FILTER_BANNER);
    if (!banner) return;

    grid.parentNode.insertBefore(clone, grid);

    // Re-query from the DOM since clone is a DocumentFragment
    this.filterBannerEl = grid.previousElementSibling;
    this._bindFilterClear();
  };

  /**
   * Binds the clear filter button click handler.
   * @private
   */
  EmperorChatbot.prototype._bindFilterClear = function () {
    if (this.filterClearBound || !this.filterBannerEl) return;

    var clearBtn = this.filterBannerEl.querySelector(SELECTORS.FILTER_CLEAR);
    if (clearBtn) {
      clearBtn.addEventListener('click', this.clearFilter.bind(this));
      this.filterClearBound = true;
    }
  };

  /** Removes the AI filter: shows all product cards and hides the banner. */
  EmperorChatbot.prototype.clearFilter = function () {
    var grid = document.querySelector(SELECTORS.PRODUCT_GRID);
    if (grid) {
      var cards = grid.querySelectorAll(SELECTORS.PRODUCT_CARD);
      for (var i = 0; i < cards.length; i++) {
        cards[i].classList.remove(CLASSES.AI_HIDDEN);
      }
    }

    if (this.filterBannerEl) {
      this.filterBannerEl.setAttribute('hidden', '');
    }
  };

  /* -----------------------------------------------------------------------
   * Product Cards: Shopify Predictive Search
   * ----------------------------------------------------------------------- */

  /**
   * Extracts unique stone names from sampleBlocks array.
   * @param {Object[]} sampleBlocks - Array with stoneName property
   * @returns {string[]}
   * @private
   */
  EmperorChatbot.prototype._extractUniqueStoneNames = function (sampleBlocks) {
    var seen = {};
    var unique = [];
    for (var i = 0; i < sampleBlocks.length; i++) {
      var name = sampleBlocks[i].stoneName;
      if (name && !seen[name]) {
        seen[name] = true;
        unique.push(name);
      }
    }
    return unique;
  };

  /**
   * Resolves a Shopify product URL for a stone name via predictive search.
   * Cached in this.productCache.
   * @param {string} stoneName
   * @returns {Promise<string|null>} - Product URL or null
   * @private
   */
  EmperorChatbot.prototype._resolveProductUrl = async function (stoneName) {
    if (this.productCache.hasOwnProperty(stoneName)) {
      return this.productCache[stoneName].url || null;
    }

    try {
      var baseUrl = (window.routes && window.routes.predictive_search_url)
        ? window.routes.predictive_search_url + '.json'
        : '/search/suggest.json';
      var url = baseUrl + '?q=' + encodeURIComponent(stoneName)
        + '&resources[type]=product&resources[limit]=5';

      var resp = await fetch(url);
      if (!resp.ok) {
        this.productCache[stoneName] = { url: null, image: null };
        return null;
      }

      var json = await resp.json();
      var products = json.resources && json.resources.results && json.resources.results.products;
      if (!products || products.length === 0) {
        this.productCache[stoneName] = { url: null, image: null };
        return null;
      }

      var lower = stoneName.toLowerCase();
      var best = products[0];
      var nameMatch = null;
      var slabMatch = null;
      for (var i = 0; i < products.length; i++) {
        var title = (products[i].title || '').toLowerCase();
        var isNameMatch = title.indexOf(lower) !== -1 || lower.indexOf(title) !== -1;
        var isSlab = title.indexOf('slab') !== -1;
        if (isNameMatch && isSlab) {
          slabMatch = products[i];
          break;
        }
        if (isNameMatch && !nameMatch) {
          nameMatch = products[i];
        }
      }
      best = slabMatch || nameMatch || best;

      var imageUrl = (best.featured_image && best.featured_image.url)
        || best.image
        || null;
      this.productCache[stoneName] = { url: best.url, image: imageUrl };
      return best.url;
    } catch (e) {
      this.productCache[stoneName] = { url: null, image: null };
      return null;
    }
  };

  /**
   * Fetches product URLs for unique stone names, then renders block cards.
   * @param {Object[]} sampleBlocks - Array of { id, code, stoneName, origin, category, availableSqm }
   */
  EmperorChatbot.prototype.fetchAndRenderBlockCards = async function (sampleBlocks) {
    var blocks = sampleBlocks.slice(0, 8);
    var self = this;

    // Resolve product URLs for unique stone names (parallel, cached)
    var uniqueNames = this._extractUniqueStoneNames(blocks);
    await Promise.all(uniqueNames.map(function (name) {
      return self._resolveProductUrl(name);
    }));

    this._renderBlockCarousel(blocks);
  };

  /**
   * Renders a horizontal carousel of block cards into the messages area.
   * Each card shows block code, stone name, origin, sqm and links to the Shopify product.
   * @param {Object[]} blocks - Array of { id, code, stoneName, origin, category, availableSqm }
   * @private
   */
  EmperorChatbot.prototype._renderBlockCarousel = function (blocks) {
    this._removeEmptyState();

    var wrapper = document.createElement('div');
    wrapper.className = 'chatbot-message chatbot-message--products';

    var scroll = document.createElement('div');
    scroll.className = 'chatbot-products__scroll';

    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      var cached = this.productCache[b.stoneName] || {};
      var productUrl = cached.url || null;
      var imageUrl = cached.image || null;

      var card = document.createElement(productUrl ? 'a' : 'div');
      card.className = 'chatbot-block-card';
      if (productUrl) {
        card.href = productUrl + (b.id ? '#block-' + b.id : '');
        card.setAttribute('target', '_blank');
        card.setAttribute('rel', 'noopener');
      }

      // Product image
      if (imageUrl) {
        var img = document.createElement('img');
        img.className = 'chatbot-block-card__img';
        img.src = imageUrl + (imageUrl.indexOf('?') !== -1 ? '&' : '?') + 'width=300';
        img.alt = b.stoneName || '';
        img.loading = 'lazy';
        card.appendChild(img);
      }

      // Block code badge
      var code = document.createElement('span');
      code.className = 'chatbot-block-card__code';
      code.textContent = b.code || '—';
      card.appendChild(code);

      // Stone name
      var name = document.createElement('span');
      name.className = 'chatbot-block-card__stone';
      name.textContent = b.stoneName || '';
      card.appendChild(name);

      // Meta line: origin + sqm
      var meta = document.createElement('span');
      meta.className = 'chatbot-block-card__meta';
      var parts = [];
      if (b.origin) parts.push(b.origin);
      if (b.availableSqm != null) parts.push(b.availableSqm + ' m\u00B2');
      meta.textContent = parts.join(' \u00B7 ');
      card.appendChild(meta);

      scroll.appendChild(card);
    }

    wrapper.appendChild(scroll);
    this.els.MESSAGES.appendChild(wrapper);
    this.scrollToBottom();
  };

  /* -----------------------------------------------------------------------
   * localStorage: History
   * ----------------------------------------------------------------------- */

  /** Loads conversation history from localStorage. */
  EmperorChatbot.prototype.loadHistory = function () {
    try {
      var raw = localStorage.getItem(STORAGE_KEYS.HISTORY);
      if (!raw) return;
      var items = JSON.parse(raw);
      if (!Array.isArray(items)) return;
      this.history = items.slice(-MAX_HISTORY);
    } catch (e) {
      this.history = [];
    }
  };

  /** Saves current conversation history to localStorage. */
  EmperorChatbot.prototype.saveHistory = function () {
    try {
      var trimmed = this.history.slice(-MAX_HISTORY);
      localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(trimmed));
    } catch (e) {
      // localStorage full or unavailable — continue without persistence
    }
  };

  /**
   * Renders previously saved messages from history into the messages container.
   * Skips error and tool messages (not persisted, but guard anyway).
   */
  EmperorChatbot.prototype.renderHistory = function () {
    if (this.history.length === 0) {
      this.showEmptyState();
      return;
    }

    this.els.MESSAGES.classList.add('chatbot-messages--no-animate');

    for (var i = 0; i < this.history.length; i++) {
      var msg = this.history[i];
      if (msg.role === 'error' || msg.role === 'tool') continue;

      var el = this.createMessageElement(msg.role);
      if (msg.role === 'assistant') {
        el.innerHTML = renderMarkdown(msg.content);
      } else {
        el.textContent = msg.content;
      }
      this.els.MESSAGES.appendChild(el);
    }

    var msgs = this.els.MESSAGES;
    requestAnimationFrame(function () {
      msgs.classList.remove('chatbot-messages--no-animate');
    });

    this.scrollToBottom();
  };

  /**
   * Pushes a message to history, trims to max, and saves.
   * @param {string} role - Message role
   * @param {string} content - Message content
   * @private
   */
  EmperorChatbot.prototype._pushHistory = function (role, content) {
    this.history.push({
      role: role,
      content: content,
      timestamp: Date.now()
    });

    if (this.history.length > MAX_HISTORY) {
      this.history = this.history.slice(-MAX_HISTORY);
    }

    this.saveHistory();
  };

  /* -----------------------------------------------------------------------
   * localStorage: Minimized State
   * ----------------------------------------------------------------------- */

  /**
   * Gets the minimized state from localStorage.
   * @returns {boolean} true if chat was previously minimized
   * @private
   */
  EmperorChatbot.prototype._getMinimizedState = function () {
    try {
      return localStorage.getItem(STORAGE_KEYS.MINIMIZED) === 'true';
    } catch (e) {
      return true;
    }
  };

  /**
   * Saves the minimized state to localStorage.
   * @param {boolean} minimized
   * @private
   */
  EmperorChatbot.prototype._saveMinimizedState = function (minimized) {
    try {
      localStorage.setItem(STORAGE_KEYS.MINIMIZED, minimized ? 'true' : 'false');
    } catch (e) {
      // localStorage unavailable
    }
  };

  /* -----------------------------------------------------------------------
   * localStorage: Fullscreen State
   * ----------------------------------------------------------------------- */

  /**
   * Gets the fullscreen preference from localStorage.
   * @returns {boolean}
   * @private
   */
  EmperorChatbot.prototype._getFullscreenState = function () {
    try {
      return localStorage.getItem(STORAGE_KEYS.FULLSCREEN) === 'true';
    } catch (e) {
      return false;
    }
  };

  /**
   * Saves the fullscreen preference to localStorage.
   * @param {boolean} fullscreen
   * @private
   */
  EmperorChatbot.prototype._saveFullscreenState = function (fullscreen) {
    try {
      localStorage.setItem(STORAGE_KEYS.FULLSCREEN, fullscreen ? 'true' : 'false');
    } catch (e) {
      // localStorage unavailable
    }
  };

  /* -----------------------------------------------------------------------
   * Clear Conversation
   * ----------------------------------------------------------------------- */

  /** Clears all chat history, messages, and filters. Resets to empty state. */
  EmperorChatbot.prototype.clearConversation = function () {
    this.history = [];
    this.saveHistory();
    this._clearMessagesDOM();
    this.clearFilter();
    this.showEmptyState();
  };

  /**
   * Removes all child elements from the messages container.
   * @private
   */
  EmperorChatbot.prototype._clearMessagesDOM = function () {
    var container = this.els.MESSAGES;
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  };

  /* -----------------------------------------------------------------------
   * Helpers
   * ----------------------------------------------------------------------- */

  /** Scrolls the messages container to the bottom. */
  EmperorChatbot.prototype.scrollToBottom = function () {
    var container = this.els.MESSAGES;
    container.scrollTop = container.scrollHeight;
  };

  /* -----------------------------------------------------------------------
   * Boot
   * ----------------------------------------------------------------------- */

  function init() {
    var config = window.__CHATBOT_CONFIG;
    if (!config || !config.apiUrl) return;

    var chatbot = new EmperorChatbot(config);
    chatbot.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
